// ─────────────────────────────────────────────────────────────────────
//  RBAC permission catalog — TypeScript mirror of the backend catalog.
//
//  LOCKSTEP: this file MUST stay in sync with
//    Shared/CustomerSupportChatSystem.Infrastructure/Authorization/Permissions.cs
//  The permission strings, the role → permission MATRIX, and the role
//  canonicalization rules are the contract. They appear in JWT role claims
//  (after canonicalization), in backend [Authorize(Policy=…)] attributes, and
//  in the frontend permission gates here. Do NOT rename a string or change a
//  matrix row without a coordinated backend + token migration.
// ─────────────────────────────────────────────────────────────────────

/** A fine-grained capability string. Mirrors Permissions.cs consts. */
export type Permission =
  | 'tenants.manage'
  | 'users.manage'
  | 'bots.manage'
  | 'config.edit'
  | 'tenantMind.view'
  | 'tenantMind.edit'
  | 'knowledge.manage'
  | 'learning.review'
  | 'memory.manage'
  | 'admin:agent_learning_management'
  | 'analytics.view'
  | 'analytics.questions.view'
  | 'analytics.cost.view'
  | 'dashboard.view'
  | 'logs.view'
  | 'diagnostics.view'
  | 'workspace.view'
  | 'workspace.handle'
  | 'workspace.viewAll';

/**
 * The four MAIN portal roles, in descending precedence order. `developer` is
 * NOT a main role — it is an additive overlay (see DEVELOPER_PERMISSIONS) any
 * main role can carry alongside their primary role.
 */
export type MainRole = 'platformAdmin' | 'admin' | 'seniorAgent' | 'agent';

/**
 * The full role → permission matrix. A user's EFFECTIVE permission set is the
 * UNION of the rows for every role they hold (roles are additive, not
 * exclusive). Keys are CANONICAL role names; look up only after
 * {@link canonicalizeRole}. Mirrors Permissions.RolePermissions in C#.
 *
 * LOCKSTEP — pinned by __tests__/auth/permissions.pin.test.ts.
 */
export const ROLE_PERMISSIONS: Readonly<Record<MainRole, readonly Permission[]>> = {
  // platformAdmin: the full catalog (all 19).
  platformAdmin: [
    'tenants.manage',
    'users.manage',
    'bots.manage',
    'config.edit',
    'tenantMind.view',
    'tenantMind.edit',
    'knowledge.manage',
    'learning.review',
    'memory.manage',
    'admin:agent_learning_management',
    'analytics.view',
    'analytics.questions.view',
    'analytics.cost.view',
    'dashboard.view',
    'logs.view',
    'diagnostics.view',
    'workspace.view',
    'workspace.handle',
    'workspace.viewAll',
  ],
  // admin (tenant admin): everything EXCEPT cross-tenant management and agent learning management.
  admin: [
    'users.manage',
    'bots.manage',
    'config.edit',
    'tenantMind.view',
    'tenantMind.edit',
    'knowledge.manage',
    'learning.review',
    'memory.manage',
    'analytics.view',
    'analytics.questions.view',
    'dashboard.view',
    'logs.view',
    'diagnostics.view',
    'workspace.view',
    'workspace.handle',
    'workspace.viewAll',
  ],
  // seniorAgent: dashboard + analytics + tenant-mind (view/edit) + knowledge + learning + live-chat workspace.
  // NOTE: admin:agent_learning_management is platformAdmin-ONLY (mirrors backend Permissions.RolePermissions).
  seniorAgent: [
    'dashboard.view',
    'analytics.view',
    'tenantMind.view',
    'tenantMind.edit',
    'knowledge.manage',
    'learning.review',
    'workspace.view',
    'workspace.handle',
    'workspace.viewAll',
  ],
  // agent: read-only operational surface + the live-chat workspace they staff.
  agent: [
    'dashboard.view',
    'analytics.view',
    'tenantMind.view',
    'workspace.view',
    'workspace.handle',
  ],
} as const;

/**
 * The additive `developer` overlay — logs + diagnostics. Union'd into the
 * effective set for any user carrying the developer role. Mirrors the
 * Developer row of Permissions.RolePermissions in C#.
 *
 * LOCKSTEP — pinned by __tests__/auth/permissions.pin.test.ts.
 */
export const DEVELOPER_PERMISSIONS: readonly Permission[] = ['logs.view', 'diagnostics.view'] as const;

/**
 * Returns the canonical form of a raw role string. Case-insensitive. Legacy
 * underscore / concatenated / alias forms map onto the canonical name. An
 * unknown role passes through UNCHANGED so it can flow through and simply
 * contribute no permissions downstream. Mirrors RoleNormalizer.Canonicalize.
 */
export function canonicalizeRole(role: string): string {
  if (role == null) return role;
  const trimmed = role.trim();
  if (trimmed.length === 0) return role;

  switch (trimmed.toLowerCase()) {
    case 'platformadmin':
    case 'platform_admin':
      return 'platformAdmin';

    case 'tenantadmin':
    case 'tenant_admin':
    case 'admin':
      return 'admin';

    case 'senioragent':
    case 'senior_agent':
      return 'seniorAgent';

    case 'agent':
      return 'agent';

    case 'developer':
      return 'developer';

    default:
      return role;
  }
}

/** Main-role precedence: highest wins when a user holds several. */
const MAIN_ROLE_PRECEDENCE: readonly MainRole[] = [
  'platformAdmin',
  'admin',
  'seniorAgent',
  'agent',
];

/**
 * Derives the single highest-precedence MAIN role from a raw role-claim array.
 * Canonicalizes every entry, then picks by precedence
 * platformAdmin > admin > seniorAgent > agent. Defaults to 'agent' when no
 * recognized main role is present. Mirrors backend main-role derivation.
 */
export function deriveMainRole(roles: readonly string[]): MainRole {
  const canonical = new Set((roles ?? []).map(canonicalizeRole));
  for (const candidate of MAIN_ROLE_PRECEDENCE) {
    if (canonical.has(candidate)) return candidate;
  }
  return 'agent';
}

/** True when any role in the array canonicalizes to the developer overlay. */
export function isDeveloperRole(roles: readonly string[]): boolean {
  return (roles ?? []).some((r) => canonicalizeRole(r) === 'developer');
}

/**
 * Computes the effective permission set (UNION across every held role, with
 * the developer overlay additive) for a raw role-claim array. Mirrors
 * Permissions.PermissionsForRoles. Unknown roles contribute nothing.
 */
export function permissionsForRoles(roles: readonly string[]): Set<Permission> {
  const effective = new Set<Permission>();
  for (const raw of roles ?? []) {
    const canonical = canonicalizeRole(raw);
    if (canonical === 'developer') {
      for (const p of DEVELOPER_PERMISSIONS) effective.add(p);
      continue;
    }
    const row = ROLE_PERMISSIONS[canonical as MainRole];
    if (row) {
      for (const p of row) effective.add(p);
    }
  }
  return effective;
}
